import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

/**
 * Requests where a 401 is a real answer, not a dead session.
 *
 * The login endpoints return 401 for a wrong password. Redirecting on those
 * would bounce someone off the login page before they ever saw "Invalid email
 * or password" — worse than no redirect at all. The public endpoints have no
 * session by design, and /auth/me is how the app *asks* whether it is signed
 * in, so its 401 is the expected answer on first load.
 */
const NO_REDIRECT = [
  /\/auth\/login$/,
  /\/auth\/logout$/,
  /\/auth\/me$/,
  /\/auth\/refresh$/,
  /\/public\//,
  /\/invitations\/accept$/,
];

/** Which sign-in page a given request belongs to. */
function loginPathFor(requestUrl) {
  const onPlatformPage = window.location.pathname.startsWith("/platform");
  const platformRequest = (requestUrl || "").includes("/platform/");
  return onPlatformPage || platformRequest ? "/platform/login" : "/login";
}

// One redirect, however many requests fail at once. A page that fires six
// parallel loads would otherwise stack six navigations.
let redirecting = false;

export function resetAuthRedirect() {
  redirecting = false;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";

    // 403 means signed in but not allowed — the user should see why, not be
    // thrown back to a login form they are already past.
    if (status !== 401 || NO_REDIRECT.some((re) => re.test(url))) {
      return Promise.reject(error);
    }

    const target = loginPathFor(url);
    const here = window.location.pathname;
    if (here !== target && !redirecting) {
      redirecting = true;
      // Remember where they were so signing back in returns them there,
      // rather than dumping everyone on the dashboard.
      const back = here + window.location.search;
      const suffix = back && back !== "/" ? `?next=${encodeURIComponent(back)}` : "";
      // assign(), not React Router: the interceptor has no router context, and
      // a full reload is the honest response to a session that is gone —
      // it clears every stale cache in memory.
      window.location.assign(`${target}${suffix}`);
    }
    return Promise.reject(error);
  },
);

export function apiError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => (typeof d?.msg === "string" ? d.msg : JSON.stringify(d))).join(" ");
  if (detail?.details?.length)
    return detail.details.map((d) => d.message).join(" · ");
  if (typeof detail?.message === "string") return detail.message;
  return String(detail);
}

export function complianceErrors(e) {
  const detail = e?.response?.data?.detail;
  return Array.isArray(detail?.details) ? detail.details : [];
}
